import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button, Modal, PillTabs } from './ui';
import { useCaseCreate, useStudentsList } from '../api/hooks';
import { classLabel } from './studentRow';
import { useAuth } from '../../utils/Auth/AuthProvider';

const CATEGORIES = [
  { key: 'academic', label: 'Academic' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'wellbeing', label: 'Wellbeing' },
  { key: 'other', label: 'Other' },
];

const LABEL = 'mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';
const MAX_RESULTS = 8;

/**
 * "Open a case" from the Cases page: pick one of the viewer's students and a category.
 * Posts the same `{ student, category }` the alert flow does. A teacher cannot open a wellbeing
 * case (the backend answers 403), so that category is not offered to them.
 */
export default function OpenCaseDialog({ isOpen, onClose, student }: {
  isOpen: boolean; onClose: () => void;
  /** Opened from a student's own page: that student, no picker. */
  student?: { id: number; name: string };
}) {
  const { role } = useAuth();
  const navigate = useNavigate();
  const students = useStudentsList({}, { enabled: isOpen && !student });
  const create = useCaseCreate();
  const [needle, setNeedle] = useState('');
  const [studentId, setStudentId] = useState<number | null>(null);
  const [category, setCategory] = useState('academic');
  const [error, setError] = useState<string | null>(null);

  const categories = role === 'teacher' ? CATEGORIES.filter((c) => c.key !== 'wellbeing') : CATEGORIES;
  const all = useMemo(() => students.data?.results ?? [], [students.data]);
  const n = needle.trim().toLowerCase();
  const matches = useMemo(
    () => all.filter((s) => !n || `${s.first_name} ${s.last_name}`.toLowerCase().includes(n) || s.username.toLowerCase().includes(n)).slice(0, MAX_RESULTS),
    [all, n],
  );
  const name = (s: { first_name: string; last_name: string; username: string }) => `${s.first_name} ${s.last_name}`.trim() || s.username;

  const chosenId = student?.id ?? studentId;
  function close() {
    setNeedle(''); setStudentId(null); setCategory('academic'); setError(null);
    onClose();
  }
  async function submit() {
    if (chosenId == null) { setError('Pick a student.'); return; }
    setError(null);
    try {
      const made = await create.mutateAsync({ student: chosenId, category });
      close();
      navigate(`/cases/${made.id}`);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 403 ? 'You do not have permission to open this case.' : 'Could not open the case. Try again.');
    }
  }

  const hint = category === 'wellbeing'
    ? 'Wellbeing cases are visible to the counselor and admins only.'
    : 'Teachers of this student, the counselor and admins can see this case.';

  return (
    <Modal isOpen={isOpen} onClose={close} title="Open a case" eyebrow="Intervention case"
      subtitle="Track what the school does to support a student."
      footer={<>
        <Button variant="ghost" onClick={close}>Cancel</Button>
        <Button onClick={submit} loading={create.isPending} disabled={chosenId == null}>Open case</Button>
      </>}>
      <div className="space-y-4">
        {error && <p role="alert" className="text-sm text-danger-ink">{error}</p>}
        {student ? (
          <div>
            <span className={LABEL}>Student</span>
            <p className="text-sm font-semibold text-ink">{student.name}</p>
          </div>
        ) : (
        <div>
          <label htmlFor="open-case-student" className={LABEL}>Student</label>
          <div className="flex min-h-[44px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-accent">
            <Search size={16} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
            <input id="open-case-student" type="search" placeholder="Find a student" value={needle}
              onChange={(e) => setNeedle(e.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-muted" />
          </div>
          <div role="group" aria-label="Students" className="mt-2 max-h-52 overflow-y-auto rounded-[12px] border border-line">
            {students.isLoading && <p className="px-3 py-3 text-sm text-muted">Loading students…</p>}
            {students.isError && <p className="px-3 py-3 text-sm text-danger-ink">Could not load students.</p>}
            {!students.isLoading && !students.isError && matches.length === 0 && <p className="px-3 py-3 text-sm text-muted">No students match.</p>}
            {matches.map((s) => (
              <button key={s.id} type="button" aria-pressed={studentId === s.id} onClick={() => setStudentId(s.id)}
                className={`flex w-full items-center justify-between gap-3 border-b border-hairline px-3 py-2 text-left text-sm last:border-b-0 ${studentId === s.id ? 'bg-navy text-white' : 'text-ink hover:bg-surface-2'}`}>
                <span className="min-w-0 truncate font-semibold">{name(s)}</span>
                <span className={`flex-none text-xs ${studentId === s.id ? 'text-on-navy' : 'text-muted'}`}>{classLabel(s.grade_name, s.section)}</span>
              </button>
            ))}
          </div>
        </div>
        )}
        <div>
          <span className={LABEL}>Category</span>
          <PillTabs label="Case category" value={category} onChange={setCategory} options={categories} />
          <p className="mt-2 text-[13px] text-muted">{hint}</p>
        </div>
      </div>
    </Modal>
  );
}
